/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx, css } from "@emotion/core";
import { Formik } from "formik";
import { Redirect } from "react-router";
import { groupBy as _groupBy, get as _get, flatten as _flatten } from "lodash";

import Form from "../../components/Form";
import { ColoredH3 } from "../../components/ColoredHeader";
import PrimaryCTAButton from "../../components/PrimaryCTA";
import CheckboxGroup, { FormikCheckbox } from "../../components/CheckboxGroup";

import { get, post } from "../api";
import { colors } from "../../theme";

import ValidationError from "../../components/ValidationError";
import PageContainer from "../components/PageContainer";
import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";

const paragraphStyle = css`
    margin: 0 auto;
`;

const spanStyle = css`
    color: ${colors.accent5};
`;

const initialValues = {
    subjects: [],
};

const validator = values => {
    let errors = {};
    if (values.subjects.length < 1) {
        errors.subjects = true;
    }
    return errors;
};

const getTutoringSubjects = async (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setTutoringSubjects
) => {
    await handleGet({
        getApiCall: () =>
            get({
                url: resourceUrls.tutoringSubjects.getEligibleTutorsSubjects,
            }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        setData: setTutoringSubjects,
        dataTransform: data => _groupBy(data, q => _get(q, "subject.name")),
    });
};

function submitSubjects(setIsLoggedIn, setError, setFormSubmissionComplete) {
    return async (values, actions) => {
        const subjectLevels = _get(values, "subjects", []).map(v => ({
            subjectId: _get(v, "subject.id"),
            levelId: _get(v, "level.id"),
        }));

        const body = {
            applicationStep: "TUTORING_SUBJECT",
            tutorSubjects: subjectLevels,
        };

        await handlePost({
            postApiCall: () =>
                post({
                    url: resourceUrls.tutoringSubjects.submitSubjects,
                    body: body,
                }),
            setIsLoggedIn: setIsLoggedIn,
            setError: setError,
            setIsSubmissionComplete: setFormSubmissionComplete,
        });

        actions.setSubmitting(false);
    };
}

const SubjectGroup = ({ subjectName, subjectLevels }) => (
    <CheckboxGroup
        additionalStyles={css`
            & h3 {
                margin-bottom: 8px;
            }
        `}
    >
        <ColoredH3 color={colors.neutral8}>{subjectName}</ColoredH3>
        {subjectLevels.map(subjectLevel => (
            <FormikCheckbox
                name="subjects"
                value={subjectLevel}
                label={_get(subjectLevel, "level.name")}
            />
        ))}
    </CheckboxGroup>
);

const TutoringSubjects = () => {
    const [tutoringSubjects, setTutoringSubjects] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    useEffect(() => {
        getTutoringSubjects(
            setIsLoading,
            setIsLoggedIn,
            setError,
            setTutoringSubjects
        );
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2>Great news!</h2>
            <p css={paragraphStyle}>
                We’ve found some subjects we’d love you to tutor. For now these
                are the only ones we’re recruiting for, and as you aced them at
                school we think you’d be a great fit (plus we offer training to
                get you back up to speed).
            </p>
            <br />
            <p css={paragraphStyle}>
                Select as many as you like – you can always edit your choices
                later.
            </p>
            <Formik
                initialValues={{
                    subjects: _flatten(Object.values(tutoringSubjects)),
                }}
                validate={validator}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={submitSubjects(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ handleSubmit, errors }) => (
                    <Form onSubmit={handleSubmit}>
                        {Object.keys(tutoringSubjects).map(subject => (
                            <SubjectGroup
                                subjectName={subject}
                                subjectLevels={tutoringSubjects[subject]}
                            />
                        ))}
                        {errors.subjects && (
                            <ValidationError data-testid="error-no-subjects-chosen">
                                Whoops! In order to complete your application,
                                you’ll need to select at least one option. Don’t
                                worry – you can always edit your choices later.
                            </ValidationError>
                        )}
                        <PrimaryCTAButton type="submit">
                            Submit application
                        </PrimaryCTAButton>
                    </Form>
                )}
            ></Formik>
        </PageContainer>
    );
};

export default TutoringSubjects;
